import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2, Images, Download, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

export default function GalerieAnsichtPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [gal, setGal] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aktivIdx, setAktivIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/galleries/${id}`);
        setGal(data.galerie);
        setFotos(data.fotos || []);
      } catch (e) { toast.error(errMsg(e)); nav("/portal"); }
      finally { setLoading(false); }
    })();
  }, [id, nav]);

  const download = async (photo_id, dateiname) => {
    try {
      const res = await api.get(`/gallery-photos/${photo_id}/full`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = dateiname || "foto";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const prev = useCallback(() => setAktivIdx((i) => (i === null ? null : (i - 1 + fotos.length) % fotos.length)), [fotos.length]);
  const next = useCallback(() => setAktivIdx((i) => (i === null ? null : (i + 1) % fotos.length)), [fotos.length]);

  useEffect(() => {
    if (aktivIdx === null) return;
    const handler = (e) => {
      if (e.key === "Escape") setAktivIdx(null);
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [aktivIdx, prev, next]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!gal) return null;

  const aktivFoto = aktivIdx !== null ? fotos[aktivIdx] : null;

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={() => nav("/portal")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Images className="h-5 w-5 text-primary" /> {gal.titel}
          </CardTitle>
          {gal.beschreibung && (
            <p className="text-sm text-muted-foreground">{gal.beschreibung}</p>
          )}
        </CardHeader>
        <CardContent>
          {fotos.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Keine Fotos vorhanden.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {fotos.map((p, idx) => (
                <button key={p.id}
                  data-testid={`galfoto-${p.id}`}
                  onClick={() => setAktivIdx(idx)}
                  className="aspect-square rounded-md overflow-hidden bg-secondary hover:shadow-md transition">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos/${p.id}/thumb`}
                    alt={p.caption || p.dateiname}
                    className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lightbox */}
      {aktivFoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
             onClick={() => setAktivIdx(null)}>
          <button onClick={(e) => { e.stopPropagation(); setAktivIdx(null); }}
            className="absolute top-4 right-4 text-white/90 hover:text-white"
            data-testid="lightbox-close">
            <X className="h-8 w-8" />
          </button>
          {fotos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10">
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10">
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos/${aktivFoto.id}/full`}
            alt={aktivFoto.caption || aktivFoto.dateiname}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded shadow-2xl" />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/60 rounded-full px-4 py-2 text-white text-sm"
            onClick={(e) => e.stopPropagation()}>
            <span>{aktivIdx + 1} / {fotos.length}</span>
            <Button size="sm" variant="outline" className="bg-white/10 text-white border-white/30"
              onClick={() => download(aktivFoto.id, aktivFoto.dateiname)}
              data-testid="lightbox-download">
              <Download className="h-4 w-4 mr-1" /> Download
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
